'use client';

/**
 * TrustpilotBadge
 *
 * Renders a Trustpilot TrustBox widget. Requires:
 * 1. A free Trustpilot Business account at https://business.trustpilot.com
 * 2. Your domain registered with Trustpilot
 * 3. The bootstrap script loaded in app/layout.tsx:
 *    <Script src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" strategy="lazyOnload" />
 *
 * Until the account is set up, this renders a neutral "Leave a review" link
 * pointing to the real Trustpilot page. We do NOT invent star ratings or
 * review counts when the widget is not live — that is a trust-risk claim.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslations } from '@/lib/i18n-client';
import { getActiveBrand } from '@/config/brands';

// Resolve the active brand's Trustpilot Business Unit ID at module load. A
// "TBD …" placeholder or empty string means the brand has no live Trustpilot
// account yet — in that case we silently skip the widget (no invented stars).
const activeTpId = getActiveBrand().tech?.trustpilot?.businessUnitId;
const ACTIVE_TRUSTPILOT_ID =
  activeTpId && !activeTpId.startsWith('TBD') ? activeTpId : '';
// Per-brand review page — derive from the active brand's canonical domain so a
// non-default brand never links to eSIMSupra's Trustpilot page (cross-brand leak).
const ACTIVE_REVIEW_DOMAIN = (
  getActiveBrand().tech?.canonical?.domain || 'esimsupra.com'
).replace(/^https?:\/\//, '').replace(/\/$/, '');
const ACTIVE_REVIEW_URL = `https://www.trustpilot.com/review/${ACTIVE_REVIEW_DOMAIN}`;
if (typeof window !== 'undefined' && !ACTIVE_TRUSTPILOT_ID && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn('[TrustpilotBadge] No Trustpilot Business Unit ID for the active brand; widget will render the neutral review link only.');
}

interface TrustpilotBadgeProps {
  templateId?: string;
  /** Your Trustpilot Business Unit ID from the Get code snippet */
  businessUnitId?: string;
  height?: string;
  theme?: 'light' | 'dark';
  locale?: string;
  /** Public Trustpilot review page (used for the fallback link). */
  reviewUrl?: string;
  className?: string;
}

export default function TrustpilotBadge({
  templateId = '5406e65db0d04a09e042d5fc', // micro star badge
  businessUnitId = ACTIVE_TRUSTPILOT_ID, // active brand id, or '' when TBD
  height = '28px',
  theme = 'dark',
  locale = 'en-US',
  reviewUrl = ACTIVE_REVIEW_URL,
  className = '',
}: TrustpilotBadgeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslations();

  useEffect(() => {
    // Re-initialize Trustpilot widget when component mounts
    if (typeof window !== 'undefined' && (window as any).Trustpilot && ref.current) {
      (window as any).Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  // If no businessUnitId yet, render a plain link to the real Trustpilot
  // page so users can leave a real review. No invented stars or counts.
  if (!businessUnitId) {
    return (
      <a
        href={reviewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ${className}`}
        aria-label={t('trustpilot.aria', 'Leave a review on Trustpilot')}
      >
        <svg
          className="w-4 h-4 text-[#00b67a]"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.958a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118L5.2 11.953a1 1 0 00-.364-1.118L1.467 9.387c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69L7.953 2.927z" />
        </svg>
        <span>{t('trustpilot.leaveReview', 'Leave a review on Trustpilot')}</span>
      </a>
    );
  }

  return (
    <div className={className}>
      <div
        ref={ref}
        className="trustpilot-widget"
        data-locale={locale}
        data-template-id={templateId}
        data-businessunit-id={businessUnitId}
        data-style-height={height}
        data-style-width="100%"
        data-theme={theme}
      >
        <a
          href="https://www.trustpilot.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Trustpilot
        </a>
      </div>
    </div>
  );
}
